import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { usePublish } from "@atlaskit/rovo-triggers";
import { UserAccount } from "../clients/gateway-client/types";
import {
  LocalStorageKey,
  SelectedSiteValue,
  addLocalStorageEventListener,
  getLocalStorageItem,
  setLocalStorageItem,
} from "../clients/localStorage";
import { useSitesWithRovoEnabled } from "../clients/gateway-client/gatewayClient";
import { AtlassianSite } from "../clients/gateway-client/types";
import { isHello } from "../utils/isHello";
import { setSentryExperiments, setSentrySiteId } from "../bootstrap/sentry";
import { useSendAnalyticEvent } from "../analytics/analyticsClient";
import { ANALYTICS_SCREEN_NAMES, ANALYTICS_UI_SUBJECTS } from "../analytics/analyticsEvents";
import RovoDisabledError from "../components/RovoDisabledError";
import { broadcastConfigToExtension } from "../utils/siteHelpers";

export type Site = {
  workspace: {
    cloudId: string;
    organisationId: string;
  };
  defaultSiteBaseUrl: string;
};

export type SiteOptions = {
  /**
   * @optional When true, allows the site state to be undefined when cleared from local storage,
   * and can resolve to a loaded state even if site is undefined. This is useful to components
   * in an unauthenticated state where site is not yet known.
   */
  allowUndefined?: boolean;
};

export type SiteContextType = {
  site: Site;
  atlassianSite: AtlassianSite;
  handleSetSite: (site: AtlassianSite) => void;
  rovoEnabledSites: AtlassianSite[];
};

export function getSelectedSiteFromLocalStorage(): Site | undefined {
  const selectedSite = getLocalStorageItem(LocalStorageKey.SELECTED_SITE);
  if (!selectedSite) {
    return;
  }

  return {
    workspace: { cloudId: selectedSite.cloudId, organisationId: selectedSite.orgId },
    defaultSiteBaseUrl: selectedSite.baseUrl,
  };
}

export const atlassianSiteToSite = (atlassianSite: AtlassianSite): Site => {
  return {
    workspace: { cloudId: atlassianSite.cloudId, organisationId: atlassianSite.orgId },
    defaultSiteBaseUrl: atlassianSite.baseUrl,
  };
};

export const findSite = (
  rovoEnabledSites: AtlassianSite[] = [],
  selectedSite: SelectedSiteValue | undefined,
): AtlassianSite | undefined => {
  // Add null check to prevent runtime errors
  if (!Array.isArray(rovoEnabledSites) || rovoEnabledSites.length === 0) {
    return undefined;
  }

  const foundSite =
    rovoEnabledSites.find((s) => s.cloudId === selectedSite?.cloudId) ??
    rovoEnabledSites.find((s) => isHello(s.cloudId)) ??
    rovoEnabledSites[0];

  return foundSite;
};

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export const useSendSiteChangeAnalytics = ({
  accountId,
}: {
  accountId: string;
}): ((args: { site: AtlassianSite; caller: string }) => void) => {
  const sendAnalyticEvent = useSendAnalyticEvent();

  const sendSiteChangeAnalytics = useCallback(
    ({ site, caller }: { site: AtlassianSite; caller: string }) => {
      sendAnalyticEvent({
        eventType: "track",
        event: {
          action: "called setSiteStatesAndBroadcast",
          actionSubject: ANALYTICS_UI_SUBJECTS.SITE_SELECTOR,
          source: ANALYTICS_SCREEN_NAMES.EXTENSION_HOME_SCREEN,
          attributes: {
            newValue: site,
            trigger: caller,
          },
        },
        userId: accountId,
      });
    },
    [accountId, sendAnalyticEvent],
  );

  return sendSiteChangeAnalytics;
};

export const SiteProvider = ({
  children,
  userAccount,
  onSiteChange,
}: {
  children: ReactNode;
  userAccount?: UserAccount;
  onSiteChange?: (site: Site) => void;
}): React.JSX.Element => {
  const lastSelectedSite = getLocalStorageItem(LocalStorageKey.SELECTED_SITE);
  const [selectedSite, setSelectedSite] = useState<SelectedSiteValue | undefined>(lastSelectedSite ?? undefined);
  const publish = usePublish("ai-mate");
  const { data, isLoading } = useSitesWithRovoEnabled(userAccount);
  const rovoEnabledSites = useMemo(() => data?.results ?? [], [data]);

  const handleSetSite = useCallback(
    (site: AtlassianSite | undefined): void => {
      if (site) {
        setLocalStorageItem(LocalStorageKey.SELECTED_SITE, {
          cloudId: site.cloudId,
          baseUrl: site.baseUrl,
          orgId: site.orgId,
        });

        // publish chat-draft to clear any conversationId (same window)
        publish({
          type: "chat-draft",
          source: "extension",
        });

        setSelectedSite(site);

        onSiteChange?.(atlassianSiteToSite(site));
      }
    },
    [publish, onSiteChange],
  );

  const atlassianSite = useMemo(() => {
    return findSite(rovoEnabledSites, selectedSite);
  }, [rovoEnabledSites, selectedSite]);

  const site = useMemo(() => {
    return atlassianSite ? atlassianSiteToSite(atlassianSite) : undefined;
  }, [atlassianSite]);

  useEffect(() => {
    if (site && atlassianSite) {
      const siteChanged = site.workspace.cloudId !== selectedSite?.cloudId;

      if (siteChanged) {
        // sets the selected site in local storage the first time the site is set
        if (!selectedSite) {
          handleSetSite({
            cloudId: site.workspace.cloudId,
            baseUrl: site.defaultSiteBaseUrl,
            siteName: atlassianSite.siteName,
            orgId: site.workspace.organisationId,
          });
        } else {
          onSiteChange?.(site);
        }
      }
      // else just broadcast the config to the extension
      broadcastConfigToExtension({
        workspace: { cloudId: site.workspace.cloudId, organisationId: site.workspace.organisationId },
        defaultSiteBaseUrl: site.defaultSiteBaseUrl,
      });
    }
  }, [selectedSite, site, atlassianSite, handleSetSite, onSiteChange]);

  const onLocalStorageChange = useCallback(
    (value: SelectedSiteValue | undefined): void => {
      if (value) {
        // tells chat to clear any conversationId
        if (value.cloudId !== selectedSite?.cloudId) {
          publish({
            type: "chat-draft",
            source: "extension",
          });
        }

        setSelectedSite(value);
      }
    },
    [publish, selectedSite],
  );

  addLocalStorageEventListener(LocalStorageKey.SELECTED_SITE, onLocalStorageChange);

  useEffect(() => {
    setSentryExperiments();

    if (site) {
      setSentrySiteId(site.workspace.cloudId);
    }
  }, [site]);

  if (isLoading) {
    // todo show loader here
    return <></>;
  }

  if (!site || !atlassianSite) {
    return <RovoDisabledError metadata={data?.metadata} showNavigation userAccount={userAccount} />;
  }

  return (
    <SiteContext.Provider value={{ site, atlassianSite, handleSetSite, rovoEnabledSites: rovoEnabledSites }}>
      {children}
    </SiteContext.Provider>
  );
};

/**
 * allowUndefined is needed because it is used in the statsig initialization
 * to allow the site to be undefined when the user is not logged in
 */
export const useSite = <T extends { allowUndefined?: boolean }>(
  options: T = {} as T,
): T["allowUndefined"] extends true ? SiteContextType | undefined : SiteContextType => {
  const context = useContext(SiteContext);

  if (!options.allowUndefined && context === undefined) {
    throw new Error("useSiteContext must be used within a SiteProvider");
  }
  return context as T["allowUndefined"] extends true ? SiteContextType | undefined : SiteContextType;
};

export type UseSiteResponse = {
  site: Site | undefined;
  isLoading: boolean;
  isFetchingLocalStorageData: boolean;
  error: Error | undefined;
};
